import type { SupportedLocale } from "@/i18n";

/**
 * Libellés i18n du module « Impression des estampes multiples » embarqués dans
 * le bundle JS, enregistrés dans le namespace de secours `print-multiples`
 * (voir `PRINT_MULTIPLES_FALLBACK_NS` / `fallbackNS` dans `i18n.ts`).
 *
 * Pourquoi : les clés `bo.print.mp.*` / `bo.preview.multipleprintTitle` sont
 * lues via `useVisibleT` (`vt(clé, libellé)`), qui affiche `libellé (clé)` tant
 * que la clé n'est pas remplie dans le catalogue backend `app`. En les
 * embarquant ici, l'UI est traduite proprement (FR/EN/ES) sans attendre — ni
 * dépendre d' — un seed en base. Si/quand ces clés sont ajoutées au catalogue
 * `translation`, elles prennent le dessus.
 */
type Dict = Record<string, string>;

export const printMultiplesFallbackResources: Record<SupportedLocale, Dict> = {
  fr: {
    "bo.preview.multipleprintTitle": "Prévisualisation estampe multiple",
    "bo.print.mp.recordToExport": "Multiple à exporter",
    "bo.print.mp.listToExport": "Liste des multiples à exporter",
    "mpPrintGroupInfosDeBase": "Infos de base",
    "mpPrintGroupDescription": "Description",
    "mpPrintGroupProduction": "Production / édition",
    "mpPrintGroupTextes": "Textes",
    "mpPrintGroupExemplaires": "Exemplaires",
    "mpPrintGroupAutres": "Autres",
    "mpPrintGroupRelations": "Enregistrements liés",
    "printDisplayOptions": "Options d'affichage",
    "printRelatedRecords": "Enregistrements liés",
    "printSecondaryImages": "Images secondaires",
    "printCoverPage": "Page titre",
    "printPageLayout": "Mise en page",
    "printPreview": "Aperçu",
    "printDisplayOptionsTitle": "Options d'affichage",
    "printCoverPageTitle": "Page titre",
    "printPageLayoutTitle": "Mise en page",
    "printPreviewTitle": "Prévisualisation",
    "printSelectModel": "Modèle d'export",
    "printDownload": "Télécharger",
    "downloadPdf": "PDF",
    "downloadWord": "Word (.docx)",
    "generatingPreview": "Génération de l'aperçu...",
    "previewGenerationError": "Erreur lors de la génération de l'aperçu",
    "clickToGeneratePreview": "Cliquez sur l'onglet pour générer l'aperçu",
    "retry": "Réessayer",
    "wordExportInProgressTitle": "Génération du document Word…",
    "wordExportInProgressDescription": "Merci de patienter, le téléchargement démarrera automatiquement.",
    "wordExportDoneTitle": "Document Word prêt",
    "wordExportErrorTitle": "Échec de la génération du document Word",
    "coverPageSavedModels": "Modèles enregistrés",
    "coverPageTitle": "Titre",
    "coverPageParagraph1": "Paragraphe 1",
    "coverPageParagraph2": "Paragraphe 2",
    "coverImageTitle": "Image de couverture",
    "coverImagePosition": "Position de l'image",
    "coverImageSize": "Taille de l'image",
    "coverImageSizeLarge": "Grande",
    "coverImageSizeSmall": "Petite",
    "coverImageFromModel": "Image du modèle",
    "coverPageUnsavedHint": "Cette page de couverture n'est pas enregistrée comme modèle. Enregistrez-la pour la réutiliser (elle s'imprimera même sans être enregistrée).",
    "saveCoverPageChanges": "Enregistrer les modifications",
    "coverPageModelName": "Nom du modèle",
    "coverPageSaveAsModel": "Enregistrer comme modèle",
    "coverPageUpdatedTitle": "Page de titre mise à jour",
    "bo.print.coverImagePositionHeader": "Avant le titre",
    "bo.print.coverImagePositionAfterTitle": "Après le titre",
    "bo.print.coverImagePositionAfterPara1": "Après le paragraphe 1",
    "bo.print.coverImagePositionFooter": "Après le paragraphe 2",
    "binderImageSize": "Taille de l'image",
    "binderImageSizeLarge": "Grande",
    "binderImageSizeSmall": "Petite",
    "textAlignment": "Alignement du texte",
    "headerAlignment": "Alignement du header",
    "imageAlignment": "Alignement de l'image",
    "alignCenter": "Centré",
    "alignLeftFull": "Aligné à gauche",
    "alignRightFull": "Aligné à droite",
    "printDocumentTitleLabel": "Titre du document",
    "printDocumentTitlePlaceholder": "Ex. Sélection expo X",
    "showPagination": "Afficher la pagination",
    "showPrintDate": "Afficher la date d'impression",
    "itemsPerPage": "Fiches par page",
    "onePerPage": "Une par page",
    "threePerPage": "Trois par page",
    "selectAll": "Tout cocher",
    "deselectAll": "Tout désélectionner",
    "noSecondaryImages": "Aucune image secondaire",
    "bo.print.mp.modelSimple": "Fiche multiple – Simple",
    "bo.print.mp.modelVignettes": "Liste de multiples – Vignettes",
    "showThumbnails": "Afficher les vignettes",
    "listSummaryFields": "Champs du résumé",
    "bo.print.mp.vignette.registrationNumber": "N° d'enregistrement",
    "bo.print.mp.vignette.artist": "Artiste",
    "bo.print.mp.vignette.title": "Titre",
    "bo.print.mp.vignette.creationDate": "Date de création",
    "bo.print.mp.vignette.dimensions": "Dimensions",
    "bo.print.mp.vignette.techniqueType": "Technique",
    "bo.print.mp.modelComplete": "Fiche multiple – Petite photo",
    "bo.print.mp.modelBinder": "Fiche multiple – Grande photo",
    "bo.print.mp.format": "Format",
    "bo.print.mp.selectionCount": "Œuvres sélectionnées",
    "bo.print.mp.exemplaireFields": "Champs des exemplaires",
    "bo.print.mp.generate": "Générer",
    "bo.print.mp.preview": "Prévisualiser",
    "bo.print.mp.download": "Télécharger",
    "bo.print.mp.backToOptions": "Modifier les options",
    "bo.print.mp.previewTitle": "Aperçu du certificat",
    "bo.print.mp.certificat": "Certificat",
    "bo.print.mp.certificatTitle": "Certificat de l'exemplaire",
    "bo.print.mp.documentNumber": "N° du document",
    "bo.print.mp.additionalParagraph": "Paragraphe additionnel",
    "bo.print.mp.oeuvreExaminee": "Œuvre examinée",
    "bo.print.mp.showLogo": "Afficher le logo",
    "bo.print.mp.showInscriptionsDos": "Inscriptions au dos",
    "bo.print.mp.signloca": "Lieu et date",
    "bo.print.mp.signlocaPlaceholder": "Fait à Paris, le ...",
    "bo.print.mp.templateStyle": "Mise en page",
    "bo.print.mp.styleStandard": "Standard",
    "bo.print.mp.styleCentered": "Centré",
    "bo.print.mp.document1": "Document 1",
    "bo.print.mp.document2": "Document 2",
    "bo.print.mp.document3": "Document 3",
    "bo.print.mp.showArtist": "Artiste / auteur",
    "bo.print.mp.showTitle": "Titre",
    "bo.print.mp.showRegistrationNumber": "N° d'enregistrement",
    "bo.print.mp.showInventoryNumber": "N° d'inventaire",
    "bo.print.mp.showSubtitle": "Sous-titre",
    "bo.print.mp.showOtherTitle": "Autre titre",
    "bo.print.mp.showCreationDate": "Date de création",
    "bo.print.mp.showFormerNumber": "Ancien numéro",
    "bo.print.mp.showOtherNumber": "Autre numéro",
    "bo.print.mp.showTechnique": "Technique / support",
    "bo.print.mp.showDimensions": "Dimensions",
    "bo.print.mp.hideDimensionLabel": "Masquer le libellé « Dimensions »",
    "bo.print.mp.showImageDimensions": "Dim. image",
    "bo.print.mp.showCopyNumber": "Copie / ex. n°",
    "bo.print.mp.hideDate": "Masquer la date d'impression",
    "bo.print.mp.showSignature": "Signature",
    "bo.print.mp.showInscriptionsBack": "Inscriptions au dos",
    "bo.print.mp.showType": "Type",
    "bo.print.mp.showEditor": "Éditeur",
    "bo.print.mp.showPrinter": "Fabricant / Imprimeur",
    "bo.print.mp.showStatus": "Statut",
    "bo.print.mp.showSummary": "Résumé",
    "bo.print.mp.showLongDescription": "Épreuves - Tirage",
    "epreuves": "Épreuves - Tirage",
    "bo.print.mp.showPublicNotes": "Notes publiques",
    "singleMultipleprintExemplaireDimensionsSheet": "Dim. avec marges",
    "singleMultipleprintExemplaireDimensionsImage": "Dim. image",
    "bo.print.mp.showSecondaryImages": "Images secondaires",
    "bo.print.mp.showExemplaires": "Lister les exemplaires",
    "bo.print.mp.oeuvresDansExposition": "Expositions",
    "bo.print.mp.oeuvresDansVentes": "Ventes publiques",
    "bo.print.mp.oeuvresDansBibliographie": "Bibliographies",
    "bo.print.mp.ex.registrationNumber": "N° d'enregistrement",
    "bo.print.mp.ex.number": "Copie / ex. n°",
    "bo.print.mp.ex.inventoryNumber": "N° d'inventaire",
    "bo.print.mp.ex.editionNumber": "N° d'édition",
    "bo.print.mp.ex.type": "Type",
    "bo.print.mp.ex.printer": "Imprimeur",
    "bo.print.mp.ex.editor": "Éditeur",
    "bo.print.mp.ex.technique": "Technique",
    "bo.print.mp.ex.support": "Support",
    "bo.print.mp.ex.signature": "Signature",
    "bo.print.mp.ex.dimensions": "Dimensions",
    "bo.print.mp.ex.status": "Statut",
    "bo.print.mp.ex.localisation": "Localisation",
    "bo.print.mp.ex.notes": "Notes",
  },
  en: {
    "bo.preview.multipleprintTitle": "Multiple print preview",
    "bo.print.mp.recordToExport": "Multiple to export",
    "bo.print.mp.listToExport": "Multiples to export",
    "mpPrintGroupInfosDeBase": "Basic info",
    "mpPrintGroupDescription": "Description",
    "mpPrintGroupProduction": "Production / edition",
    "mpPrintGroupTextes": "Texts",
    "mpPrintGroupExemplaires": "Copies",
    "mpPrintGroupAutres": "Other",
    "mpPrintGroupRelations": "Linked records",
    "printDisplayOptions": "Display options",
    "printRelatedRecords": "Linked records",
    "printSecondaryImages": "Secondary images",
    "printCoverPage": "Cover page",
    "printPageLayout": "Page layout",
    "printPreview": "Preview",
    "printDisplayOptionsTitle": "Display options",
    "printCoverPageTitle": "Cover page",
    "printPageLayoutTitle": "Page layout",
    "printPreviewTitle": "Preview",
    "printSelectModel": "Export model",
    "printDownload": "Download",
    "downloadPdf": "PDF",
    "downloadWord": "Word (.docx)",
    "generatingPreview": "Generating preview...",
    "previewGenerationError": "Error generating preview",
    "clickToGeneratePreview": "Click the tab to generate the preview",
    "retry": "Retry",
    "wordExportInProgressTitle": "Generating Word document…",
    "wordExportInProgressDescription": "Please wait, the download will start automatically.",
    "wordExportDoneTitle": "Word document ready",
    "wordExportErrorTitle": "Failed to generate Word document",
    "coverPageSavedModels": "Saved models",
    "coverPageTitle": "Title",
    "coverPageParagraph1": "Paragraph 1",
    "coverPageParagraph2": "Paragraph 2",
    "coverImageTitle": "Cover image",
    "coverImagePosition": "Image position",
    "coverImageSize": "Image size",
    "coverImageSizeLarge": "Large",
    "coverImageSizeSmall": "Small",
    "coverImageFromModel": "Image from model",
    "coverPageUnsavedHint": "This cover page isn't saved as a model. Save it to reuse it (it will print even if not saved).",
    "saveCoverPageChanges": "Save changes",
    "coverPageModelName": "Model name",
    "coverPageSaveAsModel": "Save as model",
    "coverPageUpdatedTitle": "Cover page updated",
    "bo.print.coverImagePositionHeader": "Before the title",
    "bo.print.coverImagePositionAfterTitle": "After the title",
    "bo.print.coverImagePositionAfterPara1": "After paragraph 1",
    "bo.print.coverImagePositionFooter": "After paragraph 2",
    "binderImageSize": "Image size",
    "binderImageSizeLarge": "Large",
    "binderImageSizeSmall": "Small",
    "textAlignment": "Text alignment",
    "headerAlignment": "Header alignment",
    "imageAlignment": "Image alignment",
    "alignCenter": "Centered",
    "alignLeftFull": "Left-aligned",
    "alignRightFull": "Right-aligned",
    "printDocumentTitleLabel": "Document title",
    "printDocumentTitlePlaceholder": "e.g. Show X selection",
    "showPagination": "Show pagination",
    "showPrintDate": "Show print date",
    "itemsPerPage": "Sheets per page",
    "onePerPage": "One per page",
    "threePerPage": "Three per page",
    "selectAll": "Select all",
    "deselectAll": "Deselect all",
    "noSecondaryImages": "No secondary images",
    "bo.print.mp.modelSimple": "Multiple sheet – Simple",
    "bo.print.mp.modelVignettes": "Multiple list – Thumbnails",
    "showThumbnails": "Show thumbnails",
    "listSummaryFields": "Summary fields",
    "bo.print.mp.vignette.registrationNumber": "Registration no.",
    "bo.print.mp.vignette.artist": "Artist",
    "bo.print.mp.vignette.title": "Title",
    "bo.print.mp.vignette.creationDate": "Creation date",
    "bo.print.mp.vignette.dimensions": "Dimensions",
    "bo.print.mp.vignette.techniqueType": "Technique",
    "bo.print.mp.modelComplete": "Multiple sheet – Small photo",
    "bo.print.mp.modelBinder": "Multiple sheet – Large photo",
    "bo.print.mp.format": "Format",
    "bo.print.mp.selectionCount": "Selected works",
    "bo.print.mp.exemplaireFields": "Copy fields",
    "bo.print.mp.generate": "Generate",
    "bo.print.mp.preview": "Preview",
    "bo.print.mp.download": "Download",
    "bo.print.mp.backToOptions": "Edit options",
    "bo.print.mp.previewTitle": "Certificate preview",
    "bo.print.mp.certificat": "Certificate",
    "bo.print.mp.certificatTitle": "Copy certificate",
    "bo.print.mp.documentNumber": "Document no.",
    "bo.print.mp.additionalParagraph": "Additional paragraph",
    "bo.print.mp.oeuvreExaminee": "Work examined",
    "bo.print.mp.showLogo": "Show logo",
    "bo.print.mp.showInscriptionsDos": "Inscriptions on the back",
    "bo.print.mp.signloca": "Place and date",
    "bo.print.mp.signlocaPlaceholder": "Done in Paris, on ...",
    "bo.print.mp.templateStyle": "Layout",
    "bo.print.mp.styleStandard": "Standard",
    "bo.print.mp.styleCentered": "Centered",
    "bo.print.mp.document1": "Document 1",
    "bo.print.mp.document2": "Document 2",
    "bo.print.mp.document3": "Document 3",
    "bo.print.mp.showArtist": "Artist / author",
    "bo.print.mp.showTitle": "Title",
    "bo.print.mp.showRegistrationNumber": "Registration no.",
    "bo.print.mp.showInventoryNumber": "Inventory no.",
    "bo.print.mp.showSubtitle": "Subtitle",
    "bo.print.mp.showOtherTitle": "Other title",
    "bo.print.mp.showCreationDate": "Creation date",
    "bo.print.mp.showFormerNumber": "Former number",
    "bo.print.mp.showOtherNumber": "Other number",
    "bo.print.mp.showTechnique": "Technique / medium",
    "bo.print.mp.showDimensions": "Dimensions",
    "bo.print.mp.hideDimensionLabel": 'Hide the "Dimensions" label',
    "bo.print.mp.showImageDimensions": "Image dim.",
    "bo.print.mp.showCopyNumber": "Copy / Copy No.",
    "bo.print.mp.hideDate": "Hide print date",
    "bo.print.mp.showSignature": "Signature",
    "bo.print.mp.showInscriptionsBack": "Inscriptions on the back",
    "bo.print.mp.showType": "Type",
    "bo.print.mp.showEditor": "Publisher",
    "bo.print.mp.showPrinter": "Manufacturer / Printer",
    "bo.print.mp.showStatus": "Status",
    "bo.print.mp.showSummary": "Summary",
    "bo.print.mp.showLongDescription": "Proofs – Print run",
    "epreuves": "Proofs – Print run",
    "bo.print.mp.showPublicNotes": "Public notes",
    "singleMultipleprintExemplaireDimensionsSheet": "Dim. incl. margins",
    "singleMultipleprintExemplaireDimensionsImage": "Image dim.",
    "bo.print.mp.showSecondaryImages": "Secondary images",
    "bo.print.mp.showExemplaires": "List the copies",
    "bo.print.mp.oeuvresDansExposition": "Exhibitions",
    "bo.print.mp.oeuvresDansVentes": "Public sales",
    "bo.print.mp.oeuvresDansBibliographie": "Bibliographies",
    "bo.print.mp.ex.registrationNumber": "Registration no.",
    "bo.print.mp.ex.number": "Copy / Copy No.",
    "bo.print.mp.ex.inventoryNumber": "Inventory no.",
    "bo.print.mp.ex.editionNumber": "Edition no.",
    "bo.print.mp.ex.type": "Type",
    "bo.print.mp.ex.printer": "Printer",
    "bo.print.mp.ex.editor": "Publisher",
    "bo.print.mp.ex.technique": "Technique",
    "bo.print.mp.ex.support": "Medium",
    "bo.print.mp.ex.signature": "Signature",
    "bo.print.mp.ex.dimensions": "Dimensions",
    "bo.print.mp.ex.status": "Status",
    "bo.print.mp.ex.localisation": "Location",
    "bo.print.mp.ex.notes": "Notes",
  },
  es: {
    "bo.preview.multipleprintTitle": "Previsualización de estampa múltiple",
    "bo.print.mp.recordToExport": "Múltiplo a exportar",
    "bo.print.mp.listToExport": "Lista de múltiplos a exportar",
    "mpPrintGroupInfosDeBase": "Información básica",
    "mpPrintGroupDescription": "Descripción",
    "mpPrintGroupProduction": "Producción / edición",
    "mpPrintGroupTextes": "Textos",
    "mpPrintGroupExemplaires": "Ejemplares",
    "mpPrintGroupAutres": "Otros",
    "mpPrintGroupRelations": "Registros vinculados",
    "printDisplayOptions": "Opciones de visualización",
    "printRelatedRecords": "Registros vinculados",
    "printSecondaryImages": "Imágenes secundarias",
    "printCoverPage": "Portada",
    "printPageLayout": "Diseño de página",
    "printPreview": "Vista previa",
    "printDisplayOptionsTitle": "Opciones de visualización",
    "printCoverPageTitle": "Portada",
    "printPageLayoutTitle": "Diseño de página",
    "printPreviewTitle": "Previsualización",
    "printSelectModel": "Modelo de exportación",
    "printDownload": "Descargar",
    "downloadPdf": "PDF",
    "downloadWord": "Word (.docx)",
    "generatingPreview": "Generando vista previa...",
    "previewGenerationError": "Error al generar la vista previa",
    "clickToGeneratePreview": "Haga clic en la pestaña para generar la vista previa",
    "retry": "Reintentar",
    "wordExportInProgressTitle": "Generando documento Word…",
    "wordExportInProgressDescription": "Espere, la descarga comenzará automáticamente.",
    "wordExportDoneTitle": "Documento Word listo",
    "wordExportErrorTitle": "Error al generar el documento Word",
    "coverPageSavedModels": "Modelos guardados",
    "coverPageTitle": "Título",
    "coverPageParagraph1": "Párrafo 1",
    "coverPageParagraph2": "Párrafo 2",
    "coverImageTitle": "Imagen de portada",
    "coverImagePosition": "Posición de la imagen",
    "coverImageSize": "Tamaño de la imagen",
    "coverImageSizeLarge": "Grande",
    "coverImageSizeSmall": "Pequeña",
    "coverImageFromModel": "Imagen del modelo",
    "coverPageUnsavedHint": "Esta portada no está guardada como modelo. Guárdela para reutilizarla (se imprimirá aunque no se guarde).",
    "saveCoverPageChanges": "Guardar cambios",
    "coverPageModelName": "Nombre del modelo",
    "coverPageSaveAsModel": "Guardar como modelo",
    "coverPageUpdatedTitle": "Portada actualizada",
    "bo.print.coverImagePositionHeader": "Antes del título",
    "bo.print.coverImagePositionAfterTitle": "Después del título",
    "bo.print.coverImagePositionAfterPara1": "Después del párrafo 1",
    "bo.print.coverImagePositionFooter": "Después del párrafo 2",
    "binderImageSize": "Tamaño de la imagen",
    "binderImageSizeLarge": "Grande",
    "binderImageSizeSmall": "Pequeña",
    "textAlignment": "Alineación del texto",
    "headerAlignment": "Alineación del encabezado",
    "imageAlignment": "Alineación de la imagen",
    "alignCenter": "Centrado",
    "alignLeftFull": "Alineado a la izquierda",
    "alignRightFull": "Alineado a la derecha",
    "printDocumentTitleLabel": "Título del documento",
    "printDocumentTitlePlaceholder": "Ej. Selección expo X",
    "showPagination": "Mostrar paginación",
    "showPrintDate": "Mostrar fecha de impresión",
    "itemsPerPage": "Fichas por página",
    "onePerPage": "Una por página",
    "threePerPage": "Tres por página",
    "selectAll": "Seleccionar todo",
    "deselectAll": "Deseleccionar todo",
    "noSecondaryImages": "Sin imágenes secundarias",
    "bo.print.mp.modelSimple": "Ficha de múltiplo – Simple",
    "bo.print.mp.modelVignettes": "Lista de múltiplos – Miniaturas",
    "showThumbnails": "Mostrar miniaturas",
    "listSummaryFields": "Campos del resumen",
    "bo.print.mp.vignette.registrationNumber": "N° de registro",
    "bo.print.mp.vignette.artist": "Artista",
    "bo.print.mp.vignette.title": "Título",
    "bo.print.mp.vignette.creationDate": "Fecha de creación",
    "bo.print.mp.vignette.dimensions": "Dimensiones",
    "bo.print.mp.vignette.techniqueType": "Técnica",
    "bo.print.mp.modelComplete": "Ficha de múltiplo – Foto pequeña",
    "bo.print.mp.modelBinder": "Ficha de múltiplo – Foto grande",
    "bo.print.mp.format": "Formato",
    "bo.print.mp.selectionCount": "Obras seleccionadas",
    "bo.print.mp.exemplaireFields": "Campos de los ejemplares",
    "bo.print.mp.generate": "Generar",
    "bo.print.mp.preview": "Previsualizar",
    "bo.print.mp.download": "Descargar",
    "bo.print.mp.backToOptions": "Modificar opciones",
    "bo.print.mp.previewTitle": "Vista previa del certificado",
    "bo.print.mp.certificat": "Certificado",
    "bo.print.mp.certificatTitle": "Certificado del ejemplar",
    "bo.print.mp.documentNumber": "N.º de documento",
    "bo.print.mp.additionalParagraph": "Párrafo adicional",
    "bo.print.mp.oeuvreExaminee": "Obra examinada",
    "bo.print.mp.showLogo": "Mostrar el logotipo",
    "bo.print.mp.showInscriptionsDos": "Inscripciones en el dorso",
    "bo.print.mp.signloca": "Lugar y fecha",
    "bo.print.mp.signlocaPlaceholder": "Hecho en París, el ...",
    "bo.print.mp.templateStyle": "Diseño",
    "bo.print.mp.styleStandard": "Estándar",
    "bo.print.mp.styleCentered": "Centrado",
    "bo.print.mp.document1": "Documento 1",
    "bo.print.mp.document2": "Documento 2",
    "bo.print.mp.document3": "Documento 3",
    "bo.print.mp.showArtist": "Artista / autor",
    "bo.print.mp.showTitle": "Título",
    "bo.print.mp.showRegistrationNumber": "N.º de registro",
    "bo.print.mp.showInventoryNumber": "N.º de inventario",
    "bo.print.mp.showSubtitle": "Subtítulo",
    "bo.print.mp.showOtherTitle": "Otro título",
    "bo.print.mp.showCreationDate": "Fecha de creación",
    "bo.print.mp.showFormerNumber": "Número anterior",
    "bo.print.mp.showOtherNumber": "Otro número",
    "bo.print.mp.showTechnique": "Técnica / soporte",
    "bo.print.mp.showDimensions": "Dimensiones",
    "bo.print.mp.hideDimensionLabel": "Ocultar la etiqueta «Dimensiones»",
    "bo.print.mp.showImageDimensions": "Dim. imagen",
    "bo.print.mp.showCopyNumber": "Copia / Ej. n.º",
    "bo.print.mp.hideDate": "Ocultar la fecha de impresión",
    "bo.print.mp.showSignature": "Firma",
    "bo.print.mp.showInscriptionsBack": "Inscripciones en el dorso",
    "bo.print.mp.showType": "Tipo",
    "bo.print.mp.showEditor": "Editor",
    "bo.print.mp.showPrinter": "Fabricante / Impresor",
    "bo.print.mp.showStatus": "Estado",
    "bo.print.mp.showSummary": "Resumen",
    "bo.print.mp.showLongDescription": "Pruebas – Tiraje",
    "epreuves": "Pruebas – Tiraje",
    "bo.print.mp.showPublicNotes": "Notas públicas",
    "singleMultipleprintExemplaireDimensionsSheet": "Dim. con márgenes",
    "singleMultipleprintExemplaireDimensionsImage": "Dim. imagen",
    "bo.print.mp.showSecondaryImages": "Imágenes secundarias",
    "bo.print.mp.showExemplaires": "Listar los ejemplares",
    "bo.print.mp.oeuvresDansExposition": "Exposiciones",
    "bo.print.mp.oeuvresDansVentes": "Ventas públicas",
    "bo.print.mp.oeuvresDansBibliographie": "Bibliografías",
    "bo.print.mp.ex.registrationNumber": "N.º de registro",
    "bo.print.mp.ex.number": "Copia / Ej. n.º",
    "bo.print.mp.ex.inventoryNumber": "N.º de inventario",
    "bo.print.mp.ex.editionNumber": "N.º de edición",
    "bo.print.mp.ex.type": "Tipo",
    "bo.print.mp.ex.printer": "Impresor",
    "bo.print.mp.ex.editor": "Editor",
    "bo.print.mp.ex.technique": "Técnica",
    "bo.print.mp.ex.support": "Soporte",
    "bo.print.mp.ex.signature": "Firma",
    "bo.print.mp.ex.dimensions": "Dimensiones",
    "bo.print.mp.ex.status": "Estado",
    "bo.print.mp.ex.localisation": "Ubicación",
    "bo.print.mp.ex.notes": "Notas",
  },
  nl: {
    "bo.preview.multipleprintTitle": "Voorbeeld meervoudige prent",
    "bo.print.mp.recordToExport": "Te exporteren meervoud",
    "bo.print.mp.listToExport": "Te exporteren meervouden",
    "mpPrintGroupInfosDeBase": "Basisinfo",
    "mpPrintGroupDescription": "Beschrijving",
    "mpPrintGroupProduction": "Productie / editie",
    "mpPrintGroupTextes": "Teksten",
    "mpPrintGroupExemplaires": "Exemplaren",
    "mpPrintGroupAutres": "Overig",
    "mpPrintGroupRelations": "Gekoppelde records",
    "printDisplayOptions": "Weergaveopties",
    "printRelatedRecords": "Gekoppelde records",
    "printSecondaryImages": "Secundaire afbeeldingen",
    "printCoverPage": "Titelpagina",
    "printPageLayout": "Pagina-indeling",
    "printPreview": "Voorbeeld",
    "printDisplayOptionsTitle": "Weergaveopties",
    "printCoverPageTitle": "Titelpagina",
    "printPageLayoutTitle": "Pagina-indeling",
    "printPreviewTitle": "Voorbeeld",
    "printSelectModel": "Exportmodel",
    "printDownload": "Downloaden",
    "downloadPdf": "PDF",
    "downloadWord": "Word (.docx)",
    "generatingPreview": "Voorbeeld genereren...",
    "previewGenerationError": "Fout bij genereren voorbeeld",
    "clickToGeneratePreview": "Klik op het tabblad om het voorbeeld te genereren",
    "retry": "Opnieuw",
    "wordExportInProgressTitle": "Word-document genereren…",
    "wordExportInProgressDescription": "Even geduld, de download start automatisch.",
    "wordExportDoneTitle": "Word-document gereed",
    "wordExportErrorTitle": "Genereren Word-document mislukt",
    "coverPageSavedModels": "Opgeslagen modellen",
    "coverPageTitle": "Titel",
    "coverPageParagraph1": "Paragraaf 1",
    "coverPageParagraph2": "Paragraaf 2",
    "coverImageTitle": "Omslagafbeelding",
    "coverImagePosition": "Positie afbeelding",
    "coverImageSize": "Afbeeldingsgrootte",
    "coverImageSizeLarge": "Groot",
    "coverImageSizeSmall": "Klein",
    "coverImageFromModel": "Afbeelding uit model",
    "coverPageUnsavedHint": "Deze titelpagina is niet als model opgeslagen. Sla op om te hergebruiken (wordt ook zonder opslaan afgedrukt).",
    "saveCoverPageChanges": "Wijzigingen opslaan",
    "coverPageModelName": "Modelnaam",
    "coverPageSaveAsModel": "Opslaan als model",
    "coverPageUpdatedTitle": "Titelpagina bijgewerkt",
    "bo.print.coverImagePositionHeader": "Vóór de titel",
    "bo.print.coverImagePositionAfterTitle": "Na de titel",
    "bo.print.coverImagePositionAfterPara1": "Na paragraaf 1",
    "bo.print.coverImagePositionFooter": "Na paragraaf 2",
    "binderImageSize": "Afbeeldingsgrootte",
    "binderImageSizeLarge": "Groot",
    "binderImageSizeSmall": "Klein",
    "textAlignment": "Tekstuitlijning",
    "headerAlignment": "Kopuitlijning",
    "imageAlignment": "Afbeeldingsuitlijning",
    "alignCenter": "Gecentreerd",
    "alignLeftFull": "Links uitgelijnd",
    "alignRightFull": "Rechts uitgelijnd",
    "printDocumentTitleLabel": "Documenttitel",
    "printDocumentTitlePlaceholder": "Bijv. Selectie expo X",
    "showPagination": "Paginering tonen",
    "showPrintDate": "Afdrukdatum tonen",
    "itemsPerPage": "Fiches per pagina",
    "onePerPage": "Eén per pagina",
    "threePerPage": "Drie per pagina",
    "selectAll": "Alles selecteren",
    "deselectAll": "Alles deselecteren",
    "noSecondaryImages": "Geen secundaire afbeeldingen",
    "bo.print.mp.modelSimple": "Meervoudfiche – Eenvoudig",
    "bo.print.mp.modelVignettes": "Lijst van meervouden – Miniaturen",
    "showThumbnails": "Miniaturen tonen",
    "listSummaryFields": "Samenvattingsvelden",
    "bo.print.mp.vignette.registrationNumber": "Registratienr.",
    "bo.print.mp.vignette.artist": "Kunstenaar",
    "bo.print.mp.vignette.title": "Titel",
    "bo.print.mp.vignette.creationDate": "Aanmaakdatum",
    "bo.print.mp.vignette.dimensions": "Afmetingen",
    "bo.print.mp.vignette.techniqueType": "Techniek",
    "bo.print.mp.modelComplete": "Meervoudfiche – Kleine foto",
    "bo.print.mp.modelBinder": "Meervoudfiche – Grote foto",
    "bo.print.mp.format": "Formaat",
    "bo.print.mp.selectionCount": "Geselecteerde werken",
    "bo.print.mp.exemplaireFields": "Velden van de exemplaren",
    "bo.print.mp.generate": "Genereren",
    "bo.print.mp.preview": "Voorbeeld",
    "bo.print.mp.download": "Downloaden",
    "bo.print.mp.backToOptions": "Opties wijzigen",
    "bo.print.mp.previewTitle": "Voorbeeld certificaat",
    "bo.print.mp.certificat": "Certificaat",
    "bo.print.mp.certificatTitle": "Certificaat van het exemplaar",
    "bo.print.mp.documentNumber": "Documentnr.",
    "bo.print.mp.additionalParagraph": "Aanvullende paragraaf",
    "bo.print.mp.oeuvreExaminee": "Werk onderzocht",
    "bo.print.mp.showLogo": "Logo tonen",
    "bo.print.mp.showInscriptionsDos": "Inscripties op de achterzijde",
    "bo.print.mp.signloca": "Plaats en datum",
    "bo.print.mp.signlocaPlaceholder": "Gedaan te Parijs, op ...",
    "bo.print.mp.templateStyle": "Lay-out",
    "bo.print.mp.styleStandard": "Standaard",
    "bo.print.mp.styleCentered": "Gecentreerd",
    "bo.print.mp.document1": "Document 1",
    "bo.print.mp.document2": "Document 2",
    "bo.print.mp.document3": "Document 3",
    "bo.print.mp.showArtist": "Kunstenaar / auteur",
    "bo.print.mp.showTitle": "Titel",
    "bo.print.mp.showRegistrationNumber": "Registratienr.",
    "bo.print.mp.showInventoryNumber": "Inventarisnr.",
    "bo.print.mp.showSubtitle": "Ondertitel",
    "bo.print.mp.showOtherTitle": "Andere titel",
    "bo.print.mp.showCreationDate": "Creatiedatum",
    "bo.print.mp.showFormerNumber": "Vorig nummer",
    "bo.print.mp.showOtherNumber": "Ander nummer",
    "bo.print.mp.showTechnique": "Techniek / drager",
    "bo.print.mp.showDimensions": "Afmetingen",
    "bo.print.mp.hideDimensionLabel": "Label «Afmetingen» verbergen",
    "bo.print.mp.showImageDimensions": "Beeldafm.",
    "bo.print.mp.showCopyNumber": "Kopie / Ex. nr.",
    "bo.print.mp.hideDate": "Afdrukdatum verbergen",
    "bo.print.mp.showSignature": "Handtekening",
    "bo.print.mp.showInscriptionsBack": "Inscripties op de achterzijde",
    "bo.print.mp.showType": "Type",
    "bo.print.mp.showEditor": "Uitgever",
    "bo.print.mp.showPrinter": "Fabrikant / Drukker",
    "bo.print.mp.showStatus": "Status",
    "bo.print.mp.showSummary": "Samenvatting",
    "bo.print.mp.showLongDescription": "Proeven – Oplage",
    "epreuves": "Proeven – Oplage",
    "bo.print.mp.showPublicNotes": "Openbare notities",
    "singleMultipleprintExemplaireDimensionsSheet": "Afmetingen incl. marges",
    "singleMultipleprintExemplaireDimensionsImage": "Beeldafm.",
    "bo.print.mp.showSecondaryImages": "Secundaire afbeeldingen",
    "bo.print.mp.showExemplaires": "Exemplaren opsommen",
    "bo.print.mp.oeuvresDansExposition": "Tentoonstellingen",
    "bo.print.mp.oeuvresDansVentes": "Openbare verkopen",
    "bo.print.mp.oeuvresDansBibliographie": "Bibliografieën",
    "bo.print.mp.ex.registrationNumber": "Registratienr.",
    "bo.print.mp.ex.number": "Kopie / Ex. nr.",
    "bo.print.mp.ex.inventoryNumber": "Inventarisnr.",
    "bo.print.mp.ex.editionNumber": "Editienr.",
    "bo.print.mp.ex.type": "Type",
    "bo.print.mp.ex.printer": "Drukker",
    "bo.print.mp.ex.editor": "Uitgever",
    "bo.print.mp.ex.technique": "Techniek",
    "bo.print.mp.ex.support": "Drager",
    "bo.print.mp.ex.signature": "Handtekening",
    "bo.print.mp.ex.dimensions": "Afmetingen",
    "bo.print.mp.ex.status": "Status",
    "bo.print.mp.ex.localisation": "Locatie",
    "bo.print.mp.ex.notes": "Notities",
  },
};
